import { Box, LinearProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { scanKeys } from "../../../api/queryKeys";
import { NotificationContext } from "../../../contexts/NotificationContext";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import { icons } from "../../../scenes/stack/environment/icons/iconExport";
import { ONBOARDING_STEPS, TaskStatus } from "../../../utils/constants";
import ContainedButton from "../../buttons/ContainedButton";
import PrimaryButton from "../../buttons/PrimaryButton";
import SecondaryButton from "../../buttons/SecondaryButton";
import UserInvitationForm from "../shared/UserInvitationForm";

const ScanProgressStep = () => {
  const colors = useColors();
  const onboardingContext = useOnboarding();
  const { trackEvent, getScanStatus, getIsScanning } = useAPI();
  const [progress, setProgress] = useState(10);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [scanStatus, setScanStatus] = useState(TaskStatus.IN_PROGRESS);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const { scanFinished, isScanning } = useContext(NotificationContext);

  const securityFacts = useMemo(
    () => [
      "In 2004, the FBI lost 160 laptops. None were encrypted. Good times.",
      "The average cost of a data breach in 2023 was $4.45 million.",
      "It takes an average of 277 days to identify and contain a breach.",
      "90% of successful cyberattacks start with a phishing email.",
      "Multi-factor authentication can block 99.9% of automated attacks.",
    ],
    []
  );
  const scanStepsIcons = {
    tools: {
      icon: "/onboarding/question_hub.svg",
      description: "Mapping tools and controls...",
    },
    environment: {
      icon: "/onboarding/libraries.svg",
      description: "Collecting environment data...",
    },
    gaps: {
      icon: "/onboarding/unlock.svg",
      description: "Uncovering security gaps and exposures...",
    },
    vulnerabilities: {
      icon: "/onboarding/open_door.svg",
      description: "Analyzing existing vulnerabilities....",
    },
  };

  const { data: scanData } = useQuery({
    queryKey: scanKeys.isScanning(),
    queryFn: async () => {
      const result = await getIsScanning();
      return {
        isScanning: result.data?.is_scanning,
        taskId: result.data?.task_id,
        isLoading: result.data?.is_scanning,
      };
    },
  });

  const moveToScanCompleteStep = useCallback(() => {
    try {
      onboardingContext.setOnboardingStage(ONBOARDING_STEPS.SCAN_COMPLETE);
    } catch (error) {
      console.error("Failed to move to scan complete step:", error);
    }
  }, [onboardingContext]);

  // Rotate security facts every 8 seconds
  useEffect(() => {
    const factRotationInterval = setInterval(() => {
      setCurrentFactIndex(
        (prevIndex) => (prevIndex + 1) % securityFacts.length
      );
    }, 8000);

    return () => clearInterval(factRotationInterval);
  }, [securityFacts.length]);

  // Check scan status regularly and update progress based on status
  useEffect(() => {
    let statusCheckInterval;
    let progressInterval;

    const checkScanStatus = async () => {
      if (isScanning) {
        try {
          const result = await getScanStatus(scanData?.taskId);
          const status = result.data;
          setScanStatus(status);

          // Update progress based on status
          if (
            status === TaskStatus.COMPLETED ||
            status === TaskStatus.COMPLETED_PARTIAL ||
            status === TaskStatus.FAILED ||
            status === TaskStatus.CANCELLED
          ) {
            setProgress(100);
            clearInterval(statusCheckInterval);
            clearInterval(progressInterval);

            // Give a small delay to show 100% before moving to next step
            setTimeout(() => {
              moveToScanCompleteStep();
            }, 1000);
          }
        } catch (error) {
          console.error("Failed to check scan status:", error);
        }
      }
    };

    if (isScanning && scanData?.taskId) {
      // Initial check
      checkScanStatus();

      // Set up regular status checking (every 5 seconds)
      statusCheckInterval = setInterval(checkScanStatus, 5000);

      // Set up progress bar animation that advances through steps
      // Each step takes 1/4 of the total scan time
      progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          // Calculate which step we should be on based on time remaining
          const totalTime = 30; // Initial time remaining
          const timeElapsed = totalTime - timeRemaining;
          const stepDuration = totalTime / 4; // Each step takes 1/4 of total time
          const newStepIndex = Math.min(
            3,
            Math.floor(timeElapsed / stepDuration)
          );
          setCurrentStepIndex(newStepIndex);

          // Calculate progress within current step (0-25% per step)
          const stepProgress = (timeElapsed % stepDuration) / stepDuration;
          const baseProgress = newStepIndex * 25;
          const currentStepProgress = stepProgress * 25;

          return Math.min(100, baseProgress + currentStepProgress);
        });

        setTimeRemaining((prevTime) => {
          if (prevTime <= 1) return 1;
          return prevTime - 1;
        });
      }, 1000);
    }

    // If scan is finished but we haven't moved to the next step yet,
    // update progress to 100% and move to next step
    if (!isScanning && scanFinished) {
      setProgress(100);
      setTimeout(() => {
        moveToScanCompleteStep();
      }, 1000);
    }

    return () => {
      clearInterval(statusCheckInterval);
      clearInterval(progressInterval);
    };
  }, [
    isScanning,
    scanData?.taskId,
    scanFinished,
    getScanStatus,
    moveToScanCompleteStep,
    timeRemaining,
  ]);

  const handleInviteTeammates = () => {
    trackEvent("User clicked invite teammates");
    setShowInviteForm(true);
  };

  const handleBackToScan = () => {
    trackEvent("User returned to scan from invite form");
    setShowInviteForm(false);
  };

  // Get the current step's data
  const getCurrentStep = () => {
    const stepKeys = Object.keys(scanStepsIcons);
    const currentStepKey = stepKeys[currentStepIndex];
    return scanStepsIcons[currentStepKey];
  };

  // Determine status message
  const getScanStatusMessage = () => {
    switch (scanStatus) {
      case TaskStatus.COMPLETED:
      case TaskStatus.COMPLETED_PARTIAL:
        return "Scan complete!";
      case TaskStatus.FAILED:
        return "Scan failed. Please try again.";
      case TaskStatus.CANCELLED:
        return "Scan was cancelled.";
      case TaskStatus.IN_PROGRESS:
      default:
        return `~${timeRemaining} minutes remaining`;
    }
  };

  const renderInviteForm = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mr: 1 }}>
            Invite Team Members
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Add team members to collaborate with you on the platform
        </Typography>

        <UserInvitationForm />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 4,
          }}
        >
          <SecondaryButton onClick={handleBackToScan}>
            Back to Scan
          </SecondaryButton>

          {scanStatus === TaskStatus.COMPLETED ||
          scanStatus === TaskStatus.COMPLETED_PARTIAL ||
          scanStatus === TaskStatus.FAILED ||
          scanStatus === TaskStatus.CANCELLED ||
          scanFinished ? (
            <PrimaryButton onClick={moveToScanCompleteStep}>
              Continue
            </PrimaryButton>
          ) : null}
        </Box>
      </Box>
    );
  };

  // Render the scan progress view
  const renderScanProgress = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 4,
          height: "100%",
        }}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Step 2 of 4
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 4 }}>
          Scan In Progress
        </Typography>

        <Box sx={{ width: "100%", mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: `${colors.primary_main}20`,
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.primary_main,
              },
            }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {getScanStatusMessage()}
        </Typography>

        {/* Current step indicator */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",

              color: "white",
            }}
          >
            <Box
              component="img"
              src={getCurrentStep()?.icon}
              sx={{ width: "100%", height: "auto" }}
            />
          </Box>
          <Typography variant="body1">
            {getCurrentStep()?.description || "Scanning in progress..."}
          </Typography>
        </Box>

        <Box
          sx={{
            border: `1px solid ${colors.divider}`,
            borderRadius: 2,
            p: 3,
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <SvgToIcon
              IconLogo={icons.addTeamIcon}
              style={{
                width: "24px",
                height: "24px",
                marginRight: "8px",
              }}
            />
            <Typography variant="h6" fontWeight="medium">
              Bring in your team
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3 }}>
            Invite collaborators so they can act on the findings immediately.
          </Typography>

          <ContainedButton
            color="primary"
            onClick={handleInviteTeammates}
            size="small"
          >
            Invite Teammates
          </ContainedButton>
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: colors.warning_main,
              p: 2,
              minHeight: "60px",
            }}
          >
            <span
              role="img"
              aria-label="light bulb"
              style={{ fontSize: "24px", marginRight: "8px", flexShrink: 0 }}
            >
              💡
            </span>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              {securityFacts[currentFactIndex]}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return showInviteForm ? renderInviteForm() : renderScanProgress();
};

export default ScanProgressStep;
